// components/EcoprotectorConfigurator.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    TextField,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Grid,
    Stepper,
    Step,
    StepLabel,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UpdateIcon from "@mui/icons-material/Update";
import apiClient from "../Helpers/apiClient";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";

const EcoprotectorConfigurator = () => {
    const modelTypes = ["ECP.1200", "ECP.1500", "ECP.1850", "ECP.2200", "ECP.2500", "ECP.3000", "ECP.3500"];
    const coverTypes = ["Class B", "Class D", "Class E"];

    const [modelConfig, setModelConfig] = useState({
        modelType: "ECP.1200",
        finishSurfaceLevel: 100,
        inletIL: 98,
        connectionSize: "",
        connectionType: "R",
        coverType: "Class E",
    });

    const [connectionSizes, setConnectionSizes] = useState([]);
    const [connectionTypes, setConnectionTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [isUpdated, setIsUpdated] = useState(false);
    const [errors, setErrors] = useState({});
    const [coverHeight, setCoverHeight] = useState(125);

    const steps = [
        "Configure Model",
        "Update Variables",
        "Preview 3D Model",
        "Create Drawing",
        "Download PDF",
    ];

    // Three.js refs
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const animationIdRef = useRef(null);
    const modelRef = useRef(null); // ← Current GLTF group

    const showNotification = useCallback((text, type) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 5000);
    }, []);

    const canInteractWithInputs = useCallback(() => !loading, [loading]);

    // === LOAD CONNECTION SIZES ===
    const loadConnectionSizes = useCallback(
        async (modelType) => {
            try {
                const response = await apiClient.post("/api/model/connectionsizes", modelType, {
                    headers: { "Content-Type": "application/json" },
                });

                if (response.data?.item1) {
                    setConnectionSizes(response.data.item1);
                    if (response.data.item1.length > 0 && !modelConfig.connectionSize) {
                        const firstSize = response.data.item1[0];
                        setModelConfig((prev) => ({ ...prev, connectionSize: firstSize }));
                        await loadConnectionTypes(firstSize);
                    }
                }
            } catch {
                showNotification("Failed to load connection sizes", "error");
            }
        },
        [modelConfig.connectionSize, showNotification]
    );

    // === LOAD CONNECTION TYPES ===
    const loadConnectionTypes = useCallback(
        async (connectionSize) => {
            try {
                const response = await apiClient.post("/api/model/connectiontypes", connectionSize, {
                    headers: { "Content-Type": "application/json" },
                });

                if (response.data && Array.isArray(response.data)) {
                    setConnectionTypes(response.data);
                    if (response.data.length > 0 && !modelConfig.connectionType) {
                        setModelConfig((prev) => ({ ...prev, connectionType: response.data[0] }));
                    }
                }
            } catch {
                showNotification("Failed to load connection types", "error");
            }
        },
        [modelConfig.connectionType, showNotification]
    );

    // === HANDLERS ===
    const handleModelTypeChange = useCallback(
        async (e) => {
            const value = e.target.value;
            setModelConfig((prev) => ({ ...prev, modelType: value }));
            setIsUpdated(false);
            await loadConnectionSizes(value);
        },
        [loadConnectionSizes]
    );

    const handleConnectionSizeChange = useCallback(
        async (e) => {
            const value = e.target.value;
            setModelConfig((prev) => ({ ...prev, connectionSize: value }));
            setIsUpdated(false);
            await loadConnectionTypes(value);
        },
        [loadConnectionTypes]
    );

    const handleCoverTypeChange = useCallback((coverType) => {
        const lidThickness = coverType === "Class B" ? 50 : coverType === "Class D" ? 100 : 125;
        setCoverHeight(lidThickness);
        setModelConfig((prev) => ({ ...prev, coverType }));
        setIsUpdated(false);
    }, []);

    const handleConfigChange = useCallback((updates) => {
        setModelConfig((prev) => ({ ...prev, ...updates }));
        setIsUpdated(false);
        if (updates.finishSurfaceLevel !== undefined || updates.inletIL !== undefined) {
            setErrors((prev) => ({ ...prev, fslAndInletIL: null }));
        }
    }, []);

    const validateConfig = useCallback(() => {
        const newErrors = {};
        if (modelConfig.finishSurfaceLevel <= modelConfig.inletIL) {
            newErrors.fslAndInletIL = "Finish Surface Level must be greater than Inlet IL";
        }
        if (!modelConfig.connectionSize) newErrors.connectionSize = "Connection size is required";
        if (!modelConfig.connectionType) newErrors.connectionType = "Connection type is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [modelConfig]);

    // === REMOVE OLD MODEL ===
    const clearPreviousModel = useCallback(() => {
        if (modelRef.current) {
            modelRef.current.traverse((child) => {
                if (child.isMesh) {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) child.material.forEach((m) => m.dispose());
                        else child.material.dispose();
                    }
                }
            });
            sceneRef.current?.remove(modelRef.current);
            modelRef.current = null;
        }
    }, []);

    // === THREE.JS INIT ===
    useEffect(() => {
        if (!mountRef.current) return;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf5f5f5);
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(
            75,
            mountRef.current.clientWidth / mountRef.current.clientHeight,
            0.1,
            1000
        );
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.2;
        mountRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Lighting
        scene.add(new THREE.AmbientLight(0xffffff, 0.8));

        const dirLight = new THREE.DirectionalLight(0xffffff, 2.0);
        dirLight.position.set(10, 15, 10);
        dirLight.castShadow = true;
        dirLight.shadow.mapSize.width = 2048;
        dirLight.shadow.mapSize.height = 2048;
        scene.add(dirLight);

        const fillLight = new THREE.DirectionalLight(0xffffff, 0.6);
        fillLight.position.set(-10, 5, -10); // Fixed: .position.set()
        scene.add(fillLight);

        // HDR
        new RGBELoader().setPath("/hdr/").load("studio_small_03_1k.hdr", (texture) => {
            texture.mapping = THREE.EquirectangularReflectionMapping;
            scene.environment = texture;
            scene.background = texture;
        });

        // Controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controlsRef.current = controls;

        camera.position.set(5, 5, 5);
        controls.update();

        const animate = () => {
            animationIdRef.current = requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();

        return () => {
            cancelAnimationFrame(animationIdRef.current);
            if (mountRef.current && renderer.domElement) mountRef.current.removeChild(renderer.domElement);
            renderer.dispose();
        };
    }, []);

    // === RESIZE ===
    useEffect(() => {
        const handleResize = () => {
            if (!mountRef.current || !rendererRef.current || !cameraRef.current) return;
            const { clientWidth, clientHeight } = mountRef.current;
            cameraRef.current.aspect = clientWidth / clientHeight;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(clientWidth, clientHeight);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // === LOAD GLTF ===
    const loadGLTF = async (glbBlob) => {
        if (!glbBlob || !sceneRef.current) return;
        clearPreviousModel();

        try {
            const loader = new GLTFLoader();
            const buffer = await glbBlob.arrayBuffer();
            const gltf = await loader.parseAsync(buffer, "");
            const model = gltf.scene;

            model.rotation.x = -Math.PI / 2;

            model.traverse((child) => {
                if (child.isMesh) {
                    child.geometry.computeVertexNormals(); // ← THIS REMOVES RIPPLES
                    child.material.flatShading = false;
                    child.material.needsUpdate = true;
                }
            });

            model.traverse((child) => {
                if (child.isMesh && child.material) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    child.material.metalness = 0.7;
                    child.material.roughness = 0.3;
                    child.material.envMapIntensity = 2.0;
                    child.material.needsUpdate = true;
                }
            });

            const box = new THREE.Box3().setFromObject(model);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);
            const scale = 5 / maxDim;

            model.position.sub(center.multiplyScalar(scale));
            model.scale.set(scale, scale, scale);

            sceneRef.current.add(model);
            modelRef.current = model;

            const radius = (maxDim * scale) / 2;
            const distance = radius * 2.2;
            const dir = new THREE.Vector3(1, 0.8, 1).normalize();
            cameraRef.current.position.copy(dir.multiplyScalar(distance));
            controlsRef.current.target.set(0, 0, 0);
            controlsRef.current.update();
        } catch (err) {
            console.error("GLTF load error:", err);
            showNotification("Failed to load 3D model", "error");
        }
    };

    // === INITIAL LOAD ===
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                await loadConnectionSizes(modelConfig.modelType);

                setActiveStep(1);
                showNotification("Loading initial 3D model...", "info");

                const diameter = parseInt(modelConfig.modelType.split(".")[1]);
                const data = {
                    browserConnectionId: "IamADummy",
                    params: {
                        Diameter: diameter.toString(),
                        FSL: (modelConfig.finishSurfaceLevel * 1000).toString(),
                        InvertIL: (modelConfig.inletIL * 1000).toString(),
                        InletDiameter: modelConfig.connectionSize || "150",
                        InletType: modelConfig.connectionType,
                        LidThickness: coverHeight.toString(),
                    },
                };

                await apiClient.post("/api/onshape/update-variables", data);
                await apiClient.post("/api/onshape/toggle-features", data);

                setActiveStep(2);
                const exportRes = await apiClient.post("/api/onshape/export-gltf");
                const translationId = exportRes.data.translationId;

                let glbBlob = null;
                for (let i = 0; i < 30; i++) {
                    await new Promise((r) => setTimeout(r, 1500));
                    const status = await apiClient.get(`/api/onshape/translation-status/${translationId}`);
                    if (status.data.requestState === "DONE") {
                        const resultId = status.data.resultElementIds[0];
                        const downloadRes = await apiClient.get("/api/onshape/download-gltf", {
                            params: {
                                documentId: status.data.resultDocumentId,
                                workspaceId: status.data.resultWorkspaceId,
                                elementId: resultId,
                            },
                            responseType: "blob",
                        });
                        glbBlob = downloadRes.data;
                        break;
                    }
                    if (status.data.requestState === "FAILED") throw new Error("Export failed");
                }
                if (!glbBlob) throw new Error("Export timeout");

                await loadGLTF(glbBlob);
                setIsUpdated(true);
                showNotification("Model loaded! Adjust and update.", "success");
                setActiveStep(0);
            } catch {
                showNotification("Failed to load initial model", "error");
                setActiveStep(0);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    // === UPDATE HANDLER ===
    const handleUpdate = async () => {
        if (!validateConfig()) {
            showNotification("Fix validation errors", "error");
            return;
        }

        setLoading(true);
        setActiveStep(0);
        clearPreviousModel();

        try {
            setActiveStep(1);
            showNotification("Updating...", "info");

            const diameter = parseInt(modelConfig.modelType.split(".")[1]);
            const data = {
                browserConnectionId: "IamADummy",
                params: {
                    Diameter: diameter.toString(),
                    FSL: (modelConfig.finishSurfaceLevel * 1000).toString(),
                    InvertIL: (modelConfig.inletIL * 1000).toString(),
                    InletDiameter: modelConfig.connectionSize || "150",
                    InletType: modelConfig.connectionType,
                    LidThickness: coverHeight.toString(),
                },
            };

            await apiClient.post("/api/onshape/update-variables", data);
            await apiClient.post("/api/onshape/toggle-features", data);

            setActiveStep(2);
            showNotification("Exporting glTF...", "info");

            const exportRes = await apiClient.post("/api/onshape/export-gltf");
            const translationId = exportRes.data.translationId;

            let glbBlob = null;
            for (let i = 0; i < 30; i++) {
                await new Promise((r) => setTimeout(r, 1500));
                const status = await apiClient.get(`/api/onshape/translation-status/${translationId}`);
                if (status.data.requestState === "DONE") {
                    const resultId = status.data.resultElementIds[0];
                    const downloadRes = await apiClient.get("/api/onshape/download-gltf", {
                        params: {
                            documentId: status.data.resultDocumentId,
                            workspaceId: status.data.resultWorkspaceId,
                            elementId: resultId,
                        },
                        responseType: "blob",
                    });
                    glbBlob = downloadRes.data;
                    break;
                }
            }
            if (!glbBlob) throw new Error("Export timeout");

            await loadGLTF(glbBlob);
            showNotification("Model updated!", "success");
            setIsUpdated(true);
            setActiveStep(0);
        } catch (error) {
            showNotification(error.message || "Update failed", "error");
            setActiveStep(0);
        } finally {
            setLoading(false);
        }
    };

    // === DOWNLOAD GLB ===
    const handleDownloadGLB = async () => {
        try {
            const exportRes = await apiClient.post("/api/onshape/export-gltf");
            const translationId = exportRes.data.translationId;

            let glbBlob;
            for (let i = 0; i < 30; i++) {
                await new Promise((r) => setTimeout(r, 1500));
                const status = await apiClient.get(`/api/onshape/translation-status/${translationId}`);
                if (status.data.requestState === "DONE") {
                    glbBlob = await apiClient
                        .get("/api/onshape/download-gltf", {
                            params: {
                                documentId: status.data.resultDocumentId,
                                workspaceId: status.data.resultWorkspaceId,
                                elementId: status.data.resultElementIds[0],
                            },
                            responseType: "blob",
                        })
                        .then((r) => r.data);
                    break;
                }
            }

            if (!glbBlob) throw new Error("Download timeout");

            const url = window.URL.createObjectURL(glbBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `ecoprotector_${modelConfig.modelType}.glb`;
            link.click();
            window.URL.revokeObjectURL(url);
            showNotification("GLB downloaded!", "success");
        } catch {
            showNotification("Download failed", "error");
        }
    };

    // === DOWNLOAD PDF ===
    const handleDownloadPDF = async () => {
        setLoading(true);
        setActiveStep(0);
        try {
            setActiveStep(3);
            const createRes = await apiClient.post("/api/onshape/create-drawing");
            const drawingElementId = createRes.data.drawingElementId;

            setActiveStep(4);
            const exportRes = await apiClient.post("/api/onshape/export-drawing", { drawingElementId });
            const translationId = exportRes.data.translationId;

            const pdfBlob = await pollAndDownload(translationId);
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `ecoprotector_drawing_${modelConfig.modelType}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);

            showNotification("PDF downloaded!", "success");
            setActiveStep(0);
        } catch {
            showNotification("PDF generation failed", "error");
            setActiveStep(0);
        } finally {
            setLoading(false);
        }
    };

    const pollAndDownload = async (translationId) => {
        for (let i = 0; i < 30; i++) {
            const status = await apiClient.get(`/api/onshape/translation-status/${translationId}`);
            if (status.data.requestState === "DONE") {
                return await apiClient
                    .get("/api/onshape/download-pdf", {
                        params: {
                            documentId: status.data.resultDocumentId,
                            workspaceId: status.data.resultWorkspaceId,
                            elementId: status.data.resultElementIds[0],
                        },
                        responseType: "blob",
                    })
                    .then((r) => r.data);
            }
            if (status.data.requestState === "FAILED") throw new Error(status.data.failureReason);
            await new Promise((r) => setTimeout(r, 2000));
        }
        throw new Error("PDF timeout");
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: "auto", mt: 4, p: 3 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    Ecoprotector Configurator
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                    Configure, preview in 3D, and download technical drawings
                </Typography>

                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Model Type</InputLabel>
                            <Select
                                value={modelConfig.modelType}
                                onChange={handleModelTypeChange}
                                disabled={!canInteractWithInputs()}
                            >
                                {modelTypes.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    type="number"
                                    label="Finish Surface Level (m)"
                                    value={modelConfig.finishSurfaceLevel}
                                    onChange={(e) =>
                                        handleConfigChange({ finishSurfaceLevel: parseFloat(e.target.value) })
                                    }
                                    error={!!errors.fslAndInletIL}
                                    disabled={!canInteractWithInputs()}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    type="number"
                                    label="Inlet IL (m)"
                                    value={modelConfig.inletIL}
                                    onChange={(e) =>
                                        handleConfigChange({ inletIL: parseFloat(e.target.value) })
                                    }
                                    error={!!errors.fslAndInletIL}
                                    disabled={!canInteractWithInputs()}
                                />
                            </Grid>
                        </Grid>
                        {errors.fslAndInletIL && (
                            <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
                                {errors.fslAndInletIL}
                            </Typography>
                        )}

                        <FormControl fullWidth margin="normal" error={!!errors.connectionSize}>
                            <InputLabel>Connection Size (mm)</InputLabel>
                            <Select
                                value={modelConfig.connectionSize || ""}
                                onChange={handleConnectionSizeChange}
                                disabled={connectionSizes.length === 0 || !canInteractWithInputs()}
                            >
                                {connectionSizes.length === 0 ? (
                                    <MenuItem disabled>Loading...</MenuItem>
                                ) : (
                                    connectionSizes.map((s) => (
                                        <MenuItem key={s} value={s}>
                                            {s}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <FormControl fullWidth margin="normal" error={!!errors.connectionType}>
                                    <InputLabel>Connection Type</InputLabel>
                                    <Select
                                        value={modelConfig.connectionType || ""}
                                        onChange={(e) =>
                                            handleConfigChange({ connectionType: e.target.value })
                                        }
                                        disabled={connectionTypes.length === 0 || !canInteractWithInputs()}
                                    >
                                        {connectionTypes.length === 0 ? (
                                            <MenuItem disabled>Loading...</MenuItem>
                                        ) : (
                                            connectionTypes.map((t) => (
                                                <MenuItem key={t} value={t}>
                                                    {t}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Cover Type</InputLabel>
                                    <Select
                                        value={modelConfig.coverType}
                                        onChange={(e) => handleCoverTypeChange(e.target.value)}
                                        disabled={!canInteractWithInputs()}
                                    >
                                        {coverTypes.map((t) => (
                                            <MenuItem key={t} value={t}>
                                                {t}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Stack spacing={2} sx={{ mt: 3 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handleUpdate}
                                disabled={loading}
                                startIcon={
                                    loading && activeStep === 1 ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <UpdateIcon />
                                    )
                                }
                            >
                                {loading && activeStep === 1 ? "Updating..." : "Update & Preview 3D"}
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                size="large"
                                onClick={handleDownloadGLB}
                                disabled={loading || !isUpdated}
                                startIcon={<DownloadIcon />}
                            >
                                Download GLB
                            </Button>

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                color="success"
                                onClick={handleDownloadPDF}
                                disabled={loading || !isUpdated}
                                startIcon={
                                    loading && activeStep >= 3 ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <DownloadIcon />
                                    )
                                }
                            >
                                {loading && activeStep >= 3 ? "Processing..." : "Download PDF"}
                            </Button>
                        </Stack>

                        {!isUpdated && !loading && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                align="center"
                                sx={{ mt: 2, display: "block" }}
                            >
                                Configure and click update to preview
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Paper elevation={2} sx={{ height: 500, position: "relative", overflow: "hidden" }}>
                            <Box ref={mountRef} sx={{ width: "100%", height: "100%" }} />
                            {loading && activeStep === 2 && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        bgcolor: "rgba(255,255,255,0.8)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexDirection: "column",
                                    }}
                                >
                                    <CircularProgress size={60} />
                                    <Typography sx={{ mt: 2 }}>Loading 3D Model...</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {message && (
                    <Alert severity={message.type} sx={{ mt: 2 }}>
                        {message.text}
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default EcoprotectorConfigurator;
